import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Form, Row, Col, Modal, Alert } from "react-bootstrap";
import {
  createBanner,
  updateBanner,
  fetchBannerById,
  clearError,
  clearSuccess,
} from "../../redux/slices/bannerSlice";
import { toast } from "react-toastify";
import { FaPlus, FaTimes } from "react-icons/fa";

const BannerModal = ({ show, handleClose, editId = null }) => {
  const dispatch = useDispatch();
  const isEditMode = !!editId;

  const { isCreating, isUpdating, error, currentBanner } = useSelector(
    (state) => state.banners
  );

  const [formData, setFormData] = useState({
    name: "",
    image_url: "", // Text input for image URLs (comma-separated or newline-separated)
    status: "active",
  });

  const [imageFiles, setImageFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [removedImageUrls, setRemovedImageUrls] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (isEditMode && editId && show) {
      if (!currentBanner || currentBanner.id !== editId) {
        dispatch(fetchBannerById(editId));
      }
    }
  }, [dispatch, editId, isEditMode, show, currentBanner]);

  // Populate form when currentBanner changes (for edit mode)
  useEffect(() => {
    if (isEditMode && currentBanner && currentBanner.id === editId) {
      setFormData({
        name: currentBanner.name || "",
        image_url: "", // Will be shown in existing images
        status: currentBanner.status || "active",
      });

      // Set existing images from image_urls array
      if (currentBanner.image_urls && Array.isArray(currentBanner.image_urls)) {
        setExistingImages(currentBanner.image_urls);
      } else if (currentBanner.image_url) {
        // Parse if it's a JSON string
        try {
          const parsed = JSON.parse(currentBanner.image_url);
          setExistingImages(Array.isArray(parsed) ? parsed : [parsed]);
        } catch {
          setExistingImages([currentBanner.image_url]);
        }
      } else {
        setExistingImages([]);
      }
    }
  }, [currentBanner, isEditMode, editId]);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (show) {
      if (!isEditMode) {
        resetForm();
      }
    } else {
      resetForm();
    }
  }, [show, isEditMode]);

  const resetForm = () => {
    setFormData({
      name: "",
      image_url: "",
      status: "active",
    });
    setImageFiles([]);
    setExistingImages([]);
    setRemovedImageUrls([]);
    setValidationErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // Handle file selection
  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      files.forEach((file) => {
        if (!file.type.startsWith("image/")) {
          toast.error(`${file.name} is not a valid image file`);
          return;
        }

        // Mandatory Dimension Validation: 2200 x 2000 Px
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          if (img.width !== 2200 || img.height !== 2000) {
            toast.error(`File rejected: ${file.name} must be exactly 2200 x 2000 px. (Found: ${img.width}x${img.height})`);
          } else {
            setImageFiles(prev => [...prev, file]);
          }
          URL.revokeObjectURL(objectUrl);
        };
        img.onerror = () => {
          toast.error(`Failed to load ${file.name}`);
          URL.revokeObjectURL(objectUrl);
        };
        img.src = objectUrl;
      });
    }
    // Reset target value
    e.target.value = "";
  };

  // Remove file handler
  const handleRemoveImageFile = (index) => {
    setImageFiles(imageFiles.filter((_, i) => i !== index));
  };

  // Remove existing image
  const handleRemoveExistingImage = (url) => {
    setExistingImages(existingImages.filter((img) => img !== url));
    setRemovedImageUrls([...removedImageUrls, url]);
  };

  // Add image URL from text input
  const handleAddImageUrl = () => {
    const url = formData.image_url.trim();
    if (url) {
      // Split by comma or newline
      const urls = url
        .split(/[,\n]/)
        .map((u) => u.trim())
        .filter((u) => u.length > 0);

      setExistingImages([...existingImages, ...urls]);
      setFormData((prev) => ({ ...prev, image_url: "" }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Banner name is required";
    } else if (formData.name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters long";
    }

    // Image validation - require at least one image (existing, file, or URL)
    const hasExistingImages = existingImages.length > 0;
    const hasImageFiles = imageFiles.length > 0;
    const hasImageUrl = formData.image_url.trim().length > 0;

    if (!isEditMode && !hasExistingImages && !hasImageFiles && !hasImageUrl) {
      errors.image = "At least one image is required (file upload or URL)";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix validation errors");
      return;
    }

    try {
      setIsUploading(true);

      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.name.trim());
      formDataToSend.append("status", formData.status);

      // Collect all image URLs: existing (that weren't removed) + new text URLs
      let allImageUrls = [...existingImages];
      // Add image URLs from text input if any
      if (formData.image_url.trim()) {
        const urls = formData.image_url
          .split(/[,\n]/)
          .map((u) => u.trim())
          .filter((u) => u.length > 0);
        allImageUrls = [...allImageUrls, ...urls];
      }

      // Send image_url as JSON string array if we have URLs
      if (allImageUrls.length > 0) {
        formDataToSend.append("image_url", JSON.stringify(allImageUrls));
      }

      // Add removed image URLs
      if (removedImageUrls.length > 0) {
        formDataToSend.append(
          "removedImageUrls",
          JSON.stringify(removedImageUrls)
        );
      }

      // Add new image files
      imageFiles.forEach((file) => {
        formDataToSend.append("image", file);
      });

      if (isEditMode) {
        await dispatch(
          updateBanner({ id: editId, formData: formDataToSend })
        ).unwrap();
        toast.success("Record updated successfully!!");
      } else {
        await dispatch(createBanner(formDataToSend)).unwrap();
        toast.success("Record saved successfully!!");
      }

      setTimeout(() => {
        handleModalClose();
      }, 1500);
    } catch (err) {
      console.error("Banner operation failed:", err);
      toast.error(err || "Something went wrong");
    } finally {
      setIsUploading(false);
    }
  };

  const handleModalClose = () => {
    dispatch(clearError());
    dispatch(clearSuccess());
    handleClose();
  };

  const isSubmitting = isCreating || isUpdating || isUploading;

  return (
    <Modal show={show} onHide={handleModalClose} centered size="md">
      <Form onSubmit={handleSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? `Update Banner` : "Create Banner"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert
              variant="danger"
              dismissible
              onClose={() => dispatch(clearError())}
            >
              {error}
            </Alert>
          )}

          <Row className="mb-3">
            <Col md={6} className="ps-0">
              <Form.Group>
                <Form.Label>
                  Banner Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  name="name"
                  placeholder="Enter banner name"
                  value={formData.name}
                  onChange={handleInputChange}
                  isInvalid={!!validationErrors.name}
                  disabled={isSubmitting}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.name}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col md={6} className="pe-0">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Form.Select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {/* Image URL Text Input */}

          {/* File upload field */}
          <Row className="mb-3">
            <Col md={12} className="p-0">
              <Form.Group>
                <Form.Label>Upload Images</Form.Label>
                <div className="mb-2 p-2 rounded bg-danger-soft border border-danger">
                  <p className="text-danger fw-bold mb-0 small">
                    <i className="fa-solid fa-triangle-exclamation me-2"></i>
                    IMPORTANT NOTE: Each image must be exactly 2200 x 2000 pixels.
                  </p>
                </div>
                <Form.Control
                  id="file-uplad"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageSelect}
                  disabled={isSubmitting}
                  isInvalid={!!validationErrors.image}
                />
                <Form.Control.Feedback type="invalid">
                  {validationErrors.image}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  You can upload multiple image files
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>

          {/* Existing Images Preview */}
          {existingImages.length > 0 && (
            <Row className="mb-3">
              <Col md={12} className="p-0">
                <Form.Label>Existing Images</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {existingImages.map((url, index) => (
                    <div key={index} className="position-relative">
                      <img
                        src={url}
                        alt={`Banner ${index + 1}`}
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "cover",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 m-1"
                        style={{ borderRadius: "50%", padding: "2px 6px" }}
                        onClick={() => handleRemoveExistingImage(url)}
                        disabled={isSubmitting}
                      >
                        <FaTimes />
                      </Button>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          )}

          {/* New Image Files Preview */}
          {imageFiles.length > 0 && (
            <Row className="mb-3">
              <Col md={12} className="p-0">
                <Form.Label>New Images to Upload</Form.Label>
                <div className="d-flex flex-wrap gap-2">
                  {imageFiles.map((file, index) => (
                    <div key={index} className="position-relative">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Preview ${index + 1}`}
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "cover",
                          borderRadius: "4px",
                          border: "1px solid #ddd",
                        }}
                      />
                      <Button
                        variant="danger"
                        size="sm"
                        className="position-absolute top-0 end-0 m-1"
                        style={{ borderRadius: "50%", padding: "2px 6px" }}
                        onClick={() => handleRemoveImageFile(index)}
                        disabled={isSubmitting}
                      >
                        <FaTimes />
                      </Button>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            className="btn-outline-danger-custom"
            onClick={handleModalClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="btn-outline-success"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting
              ? "Processing..."
              : isEditMode
                ? "Update Banner"
                : "Create Banner"}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default BannerModal;
